// src/components/JourneyStepper.js
import React from "react";
import { Stepper, Step, StepLabel, Box } from "@mui/material";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { styled, keyframes } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import { RoleMarker } from "./session/StepToolbar";

// Every icon sits in a slot of the same size, so all labels line up on one row
// whatever the state (upcoming, active, completed, AI).
const SLOT = 40;

/**
 * Gradient connector between steps, centred on the icon slot
 */
const JourneyConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: SLOT / 2 - 1,
    left: `calc(-50% + ${SLOT / 2 + 4}px)`,
    right: `calc(50% + ${SLOT / 2 + 4}px)`,
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor:
      theme.palette.mode === "dark"
        ? theme.palette.grey[700]
        : theme.palette.grey[300],
    borderTopWidth: 2,
    borderRadius: 1,
    transition: "all 0.2s ease-out",
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderImage: "linear-gradient(90deg, #6366f1, #22c55e, #f97316) 1",
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderImage: "linear-gradient(90deg, #22c55e, #22c55e) 1",
  },
}));

const Slot = styled("div")({
  width: SLOT,
  height: SLOT,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
});

/**
 * Rounded icon for normal steps (completed / active / upcoming)
 */
const StepIconRoot = styled("div")(({ theme, ownerState }) => {
  const { active, completed } = ownerState;
  const size = active ? 32 : 28;

  return {
    position: "relative",
    backgroundColor: completed
      ? "#000000"
      : active
        ? theme.palette.primary.light
        : theme.palette.grey[300],
    color:
      completed || active
        ? theme.palette.common.white
        : theme.palette.text.secondary,
    zIndex: 1,
    width: size,
    height: size,
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 600,
    boxShadow: active
      ? `0 0 0 4px ${theme.palette.primary.light}33`
      : "none",
    transition: "all 0.2s ease-out",
  };
});

// A slow, soft pulse for the AI glow
const aiPulse = keyframes`
  0%, 100% { opacity: 0.75; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
`;

/**
 * Colorful "AI" icon with an even, circular glow behind it
 */
const AiIconRoot = styled("div")(() => ({
  position: "relative",
  width: 32,
  height: 32,
  zIndex: 1,

  // Glow: a blurred radial halo, the same in every direction
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -9,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(147,51,234,0.45) 0%, rgba(56,189,248,0.30) 45%, rgba(255,110,196,0.12) 65%, rgba(255,255,255,0) 72%)",
    filter: "blur(3px)",
    animation: `${aiPulse} 3s ease-in-out infinite`,
    zIndex: 0,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },

  ".ai-ring": {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    background:
      "conic-gradient(from 180deg, #ff6ec4, #6366f1, #22c55e, #fbbf24, #ff6ec4)",
    zIndex: 1,
  },

  ".ai-inner": {
    position: "absolute",
    inset: 3,
    borderRadius: "50%",
    background:
      "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.25), rgba(15,23,42,0.95))",
    zIndex: 2,
  },

  ".ai-label": {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3,
    color: "#ffffff",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: 1,
  },
}));

function AiIcon() {
  return (
    <AiIconRoot>
      <div className="ai-ring" />
      <div className="ai-inner" />
      <span className="ai-label">AI</span>
    </AiIconRoot>
  );
}

/**
 * CustomStepIcon
 *
 * Logic:
 *  - For AI steps (5 and 7):
 *      • Before that step: normal numbered icon (upcoming).
 *      • While on that step (active): AI icon.
 *      • After that step (completed): normal numbered icon with check.
 *  - For all other steps: normal numbered icon as before.
 */
function CustomStepIcon(props) {
  const { active, completed, className, icon, isAiStep } = props;

  if (isAiStep && active) {
    return (
      <Slot className={className}>
        <AiIcon />
      </Slot>
    );
  }

  const numericIcon =
    typeof icon === "number" || typeof icon === "string"
      ? String(Number(icon) - 1) // keep your 0–7 numbering
      : icon;

  return (
    <Slot className={className}>
      <StepIconRoot ownerState={{ active, completed }}>
        <span className="step-number">{numericIcon}</span>
        {completed && (
          <CheckIcon
            sx={{
              position: "absolute",
              bottom: -4,
              right: { xs: -5, sm: -9 },
              fontSize: 13,
              p: "1px",
              color: "#22c55e",
              backgroundColor: "#000000",
              border: "1.5px solid #fff",
              borderRadius: "50%",
            }}
          />
        )}
      </StepIconRoot>
    </Slot>
  );
}

/**
 * JourneyStepper
 * Props:
 *  - steps: string[]
 *  - activeStep: number
 *  - hasAcknowledgedStep5: boolean (still used up in App, but not needed for icon now)
 *  - hasAcknowledgedStep7: boolean
 */
export default function JourneyStepper({
  steps,
  activeStep,
  hasAcknowledgedStep5, // kept for compatibility, not used here
  hasAcknowledgedStep7,  // kept for compatibility, not used here
}) {
  // Steps whose content is AI-generated (we only show the AI icon when they are active)
  const AI_STEP_INDICES = [5, 7];

  return (
    <Stepper
      activeStep={activeStep}
      alternativeLabel
      connector={<JourneyConnector />}
      sx={{ px: 1, py: 1, overflow: "visible" }}
    >
      {steps.map((label, index) => (
        <Step key={label} sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
          <StepLabel
            StepIconComponent={(iconProps) => (
              <CustomStepIcon
                {...iconProps}
                isAiStep={AI_STEP_INDICES.includes(index)}
              />
            )}
            sx={{
              "& .MuiStepLabel-label": {
                display: { xs: "none", sm: "block" },
                fontSize: { sm: "0.75rem", md: "0.7rem", lg: "0.75rem" },
                lineHeight: 1.35,
                textAlign: "center",
                mt: "6px !important",
                whiteSpace: { sm: "normal", md: "nowrap" },
                fontWeight: index === activeStep ? 700 : 400,
                color: (theme) =>
                  index === activeStep
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
              },
            }}
          >
            {label}
            {/* Who acts at this step: same markers as Step 6 (👤 you, 🤖 AI, 🤖 + 👤 both) */}
            <Box sx={{ mt: 0.5, minHeight: "1.1rem", fontWeight: 400 }}>
              <RoleMarker step={index} />
            </Box>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}
